import * as React from "react";
import { useAuthContext } from "./useAuthContext";
import { useOrganisation } from "@hooks/crud/useOrganisation";
import { OrganisationLineItem } from "@interfaces/organisation";
import { ProfileLineItem } from "@interfaces/profile";
import { useProfile } from "@hooks/crud/useProfile";
import { PageLoader } from "@stories/atoms/Loaders/PageLoader/PageLoader";

type PC = {
  userProfile: ProfileLineItem | null;
  userOrganisation: OrganisationLineItem | null;
  error?: string;
  isProfileLoading: boolean;
  createUserProfile: (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => Promise<void>;
  updateUserProfile: (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => Promise<void>;
};

export const ProfileContext = React.createContext<PC>({
  userProfile: null,
  userOrganisation: null,
  error: "Context not initialised correctly.",
  isProfileLoading: false,
  createUserProfile: () => Promise.resolve(),
  updateUserProfile: () => Promise.resolve(),
});

type ProjectProviderProps = {
  children: React.ReactNode;
};

const ProfileProvider = (props: ProjectProviderProps) => {
  const { userProfile, isAuthLoading, setUserProfile } = useAuthContext();

  const { createProfile, updateProfile } = useProfile();

  const {
    isOrganisationLoading,
    userOrganisation,
    getProfileOrganisation,
    createOrganisation,
    updateOrganisation,
  } = useOrganisation();

  const createUserProfile = async (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => {
    await createOrganisation(organisation);
    await createProfile(profile);
    setUserProfile(profile);
  };

  const updateUserProfile = async (
    organisation: OrganisationLineItem,
    profile: ProfileLineItem
  ) => {
    await updateOrganisation(organisation);
    await updateProfile(profile);
    setUserProfile(profile);
  };

  React.useEffect(() => {
    if (userProfile) {
      getProfileOrganisation(userProfile.organisationId);
    }
  }, [userProfile]);

  return (
    <ProfileContext.Provider
      value={{
        userProfile,
        isProfileLoading: isAuthLoading || isOrganisationLoading,
        createUserProfile,
        updateUserProfile,
        userOrganisation,
      }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
};

const useProfileContext = () => React.useContext(ProfileContext);

export { ProfileProvider, useProfileContext };
